import * as Actions from 'actions'
import { debounce, isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'
import cx from 'classnames'
import { connect, useSelector } from 'react-redux'
import { SliderTimeContext } from 'contexts/SliderTimeContext'
import { FILE_STATUS, TRANSITION_MOUNT_TYPE } from '~/enums'
import { ImageAsset, TextAsset, VideoAsset, MediaRecordingAsset, AudioAsset, TransitionAsset } from '~/models/Asset'
import * as PT from '~/PropTypes'
import * as Selectors from '~/selectors'
import { getCachedThumbnail } from '~/ServerAPI'
import { isCtrlKeyPressed, normalizeThumbnail, time2Pixel, pixel2Time, getWindowWidth } from '~/Util'
import { isResizableAsset } from '~/Util/assets'
import { getAssetMediaThumbnailFromBackend } from '~/helpers/assets/getAssetMediaThumbnailFromBackend'
import { Context as TimelineGeometryContext } from '../GeometryContextProvider'
import { TimelineScrollPositionContext } from '../ScrollPositionContext'
import AssetDropTarget from './AssetDropTarget'
import DragTransitionContainer from './DragTransitionContainer'
import { Expander } from './Expander'
import './LayerItem.scss'
import { TRANSITION_MIN_WIDTH } from './TransitionAsset'
import { isVoiceover } from '~/Util/recording'
import { MIN_TIMELINE_ITEM_DURATION_PX } from '~/constant'

import styles from './layerItem.module.scss'
import { isOverlapAssets } from '~/Util/RangeTools'
import { isCapturedAsset, stopLiveCapture } from '../../../actions/liveCapture'
import { useAction } from '../../../hooks'

const MUTED_AUDIO_TRACK_COLOR = '#43745f'
const REGULAR_AUDIO_TRACK_COLOR = '#50ffc0'
const MAX_SIMULTANIOUS_THUMBS_REQ_COUNT = 5

function getAudioTrackStyle(enabled = true) {
  const color = enabled ? REGULAR_AUDIO_TRACK_COLOR : MUTED_AUDIO_TRACK_COLOR
  return `linear-gradient( rgba(0,0,0,0) 0px, rgba(0,0,0,0) 50px, ${color} 50px, ${color} 100%),`
}

function getAudioThumbnail(asset) {
  return isVoiceover(asset) ? styles.voiceItem : styles.audioItem
}

function getAudioBorderStyle(asset) {
  return isVoiceover(asset) ? styles.voiceItemBorder : styles.audioItemBorder
}

// TODO: rework component to divide logic of different asset types by separate components
class LayerItem extends React.PureComponent {

  constructor(props) {
    super(props)
    const { connectDragSource } = this.props

    // @link https://docs.google.com/document/d/1vsdg-XX2805oFctMGgXkvJPQAes0kQJLksRKMBejKHg/edit?ts=5e997f22#bookmark=id.p2oawjx3ukgl
    this.thumbWidth = 103
    this.thumbHeight = 58

    this.width = 0

    this.scrollTimer = null

    this.mouseDownXRef = React.createRef(null)

    this.abortController = new AbortController()
    this.itemRef = React.createRef()
    this.timeOut = React.createRef()
    this.currentLoadPromise = null
    if (connectDragSource) {
      connectDragSource(this.itemRef)
    }
    // timeout before getting thumbnails when scale is changed,
    // because dragging of scale slider will produce huge amount
    // of thumbnails requests
    this.loadThumbnailsToken = 0
    this.reloadThumbnails = debounce(async () => {
      this.abortController.abort()
      this.abortController = new AbortController()
      this.loadThumbnailsToken += 1
      const token = this.loadThumbnailsToken
      if (this.currentLoadPromise) {
        try {
          await this.currentLoadPromise
        } catch (e) {
          // Ignore abort-related errors
        }
      }
      if (token !== this.loadThumbnailsToken) {
        return
      }
      this.initItemWidth()
      this.currentLoadPromise = this.loadCachedThumbnails(token)
    }, 200)
  }

  state = {
    thumbnailsLoaded: false,
    thumbnails: {},
    hover: false,
    isResizing: false,
    // eslint-disable-next-line react/no-unused-state
    computedStyles: {},
  }

  render() {
    const { asset, isDragging, transitionLeft, transitionRight, isCapture,
      mediaRecordingAssetInProgress, onClick, asDragPreview, isMovingSlider,
      selectedAssetsIds, onTranslate } = this.props

    const { isResizing } = this.state
    const rootStyle = this.getRootStyles()
    const { width, borderLeftWidth } = rootStyle

    return (
      <AssetDropTarget
        asset={asset}
        isDragging={isDragging}
        ref={this.connectAssetDropTarget}
        style={rootStyle}
        onClick={this.onClick}
        onMouseDown={this.onMouseDown}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
        borderLeftWidth={borderLeftWidth}
        selectedAssetsIds={selectedAssetsIds}
        mediaRecordingAssetInProgress={mediaRecordingAssetInProgress}
      >
        <If condition={this.isResizable()
          && !asDragPreview
          && !isCapture
          && !(asset instanceof MediaRecordingAsset)
          && (!isMovingSlider || asset.selected)}
        >
          <Expander
            onStartExpanding={this.startResizing}
            onEndExpanding={this.endResizing}
            asset={asset}
            isDragging={isResizing}
            left={transitionLeft}
            right={transitionRight}
          />
        </If>
        <If condition={asset instanceof ImageAsset && !asset.mediaDeleted}>
          <div className="layer-item--image__text" style={{ marginLeft: `${this.thumbWidth + 4}px` }}>{asset.name}</div>
        </If>
        <If condition={asset.mediaDeleted}>
          <div className="layer-item--deleted">
            {/* this width / length of one deleted message item */}
            {new Array(Math.max(1, Math.ceil(this.width / 104))).fill(0).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`deletedMedia-${asset.id}-${index}`} className="layer-item--deleted__elem">
                <div className="layer-item--deleted__elem__icon">!</div>
                <div className="layer-item--deleted__elem__text">{onTranslate?.('MEDIA_ERROR_not_found')}</div>
              </div>
            ))}
          </div>
        </If>
        <If condition={(asset instanceof AudioAsset || asset instanceof MediaRecordingAsset)
            && !asset.mediaDeleted}
        >
          <div className={cx(styles.audioMainContainer, getAudioBorderStyle(asset))}>
            <div className={cx(styles.audioContainer, getAudioThumbnail(asset))} />
            <div className={styles.audioTextContainer}>
              <div className={cx(styles.audioIconBox,
                { [styles.voiceIcon]: isVoiceover(asset),
                  [styles.audioIcon]: !isVoiceover(asset) })}
              />
              <div className={styles.audioName}>{asset.name}</div>
            </div>
          </div>
        </If>
        <If condition={asset instanceof TextAsset}>
          <div className="layer-item--text__text">{asset.text}</div>
        </If>
        <If condition={asset.canHaveTransition}>
          <If condition={
            transitionLeft !== null
            && transitionLeft.mountType === TRANSITION_MOUNT_TYPE.IN
          }
          >
            <DragTransitionContainer
              key={transitionLeft.id}
              onClick={onClick}
              asset={transitionLeft}
              assetWidth={width}
              borderLeftWidth={borderLeftWidth}
            />
          </If>
          <If condition={transitionRight !== null}>
            <DragTransitionContainer
              key={transitionRight.id}
              onClick={onClick}
              asset={transitionRight}
              assetWidth={width}
              borderLeftWidth={borderLeftWidth}
            />
          </If>
        </If>
      </AssetDropTarget>
    )
  }

  componentDidMount() {
    const { asset, scale } = this.props
    if (asset?.duration) {
      this.width = time2Pixel(asset.duration, scale)
    }
    if (this.context) {
      const { subscribe } = this.context
      this.unsubscribeLiveDuration = subscribe(() => {
        if (this.isRecordingAssetLive) this.forceUpdate()
      })
    }
    if (this.shouldLoadThumbnails) {
      this.reloadThumbnails()
    } else if (this.shouldLoadAssetMediaThumbnail) {
      this.loadAssetMediaThumbnail()
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { scale, timelineScrollPosition, asset, isProjectEditing } = this.props

    if (this.shouldLoadThumbnails) {
      if (isProjectEditing && !prevState.thumbnailsLoaded) {
        this.reloadThumbnails.cancel()
        this.abortController.abort()
        return
      }

      const { progress } = asset
      const { thumbnails } = this.state
      if (prevProps.scale !== scale
        || prevProps.asset.mediaStart !== asset.mediaStart
        || (asset.mediaDeleted && !isEmpty(thumbnails))) {
        // eslint-disable-next-line
        this.setState({
          thumbnailsLoaded: false,
          thumbnails: {},
          // eslint-disable-next-line react/no-unused-state
          computedStyles: this.getRootStyles({
            nextThumbnails: {},
            nextLoaded: false,
          }),
        })
      }

      if (!asset.mediaDeleted) {
        // eslint-disable-next-line react/destructuring-assignment
        if ((!this.state.thumbnailsLoaded && !prevState.thumbnailsLoaded)
          || prevProps.scale !== scale
          || prevProps.timelineScrollPosition !== timelineScrollPosition
          || prevProps.asset.mediaStart !== asset.mediaStart
          || prevProps.asset.duration !== asset.duration
          || prevProps.asset.progress !== progress) {
          this.reloadThumbnails.cancel()
          this.reloadThumbnails()
        }
      }
    }
  }

  componentWillUnmount() {
    if (this.unsubscribeLiveDuration) this.unsubscribeLiveDuration()
    this.abortController.abort()
  }

  onClick = e => {
    // don't send event to the parent (Layer)
    e.stopPropagation()
    e.persist()
    // do not execute if double clicked. e.detail === 1 - is single click
    if (this.timeOut) clearTimeout(this.timeOut)
    setTimeout(() => {
      // protection from asset resizing click
      if (this.mouseDownXRef.current === e.pageX && (e.detail === 1)) {
        const { asset, onClick } = this.props
        onClick(asset.id, {
          ctrlKey: isCtrlKeyPressed(e),
          shiftKey: e.shiftKey,
        })
      }
    }, 80)
  }

  onMouseDown = e => {
    const {
      mediaRecordingAssetInProgress,
      stopRecording,
      asset,
      toggleSelect,
      selectedAssetsIds,
      isCapture,
      isCapturedAsset,
      stopCapture,
    } = this.props
    e.persist()
    if (!isCapturedAsset(asset) && isCapture) {
      stopCapture()
    }
    if (!isCapture && mediaRecordingAssetInProgress && asset.url) {
      stopRecording()
    }
    this.mouseDownXRef.current = e.pageX
    if (!selectedAssetsIds.includes(asset.id)) {
      if (!isCtrlKeyPressed(e) && !e.shiftKey) {
        toggleSelect(asset.id)
      }
    }
  }

  onMouseEnter = () => {
    this.setState({ hover: true })
  }

  onMouseLeave = () => {
    this.setState({ hover: false })
  }

  isResizable() {
    const { asset, scale, transitionLeft, transitionRight } = this.props
    const { isResizing } = this.state

    const trlWidth = time2Pixel(transitionLeft?.duration || 0, scale)
    const trrWidth = time2Pixel(transitionRight?.duration || 0, scale)
    const width = time2Pixel(asset.duration, scale)
    if (isResizing) return true

    return Math.round((width - trlWidth - trrWidth) * 100) / 100 >= MIN_TIMELINE_ITEM_DURATION_PX
      && isResizableAsset(asset)
  }

  getTransitionGeometryOffset() {
    const { scale, transitionRight, transitionLeft } = this.props
    const geometry = { left: 0, width: 0 }
    if (transitionRight) {
      if (transitionRight.isMountBetween
        || transitionRight.isMountOut) {
        const width = time2Pixel(transitionRight.duration, scale)
        geometry.width = Math.max(TRANSITION_MIN_WIDTH, width)
      }
    }

    if (transitionLeft) {
      if (transitionLeft.isMountBetween
      || transitionLeft.isMountIn) {
        const width = time2Pixel(transitionLeft.duration, scale)
        geometry.left = Math.max(TRANSITION_MIN_WIDTH, width)
        geometry.width += geometry.left
      }
    }
    // el.style.backgroundPositionX = `${-transitionWidth}px, 80px, 185px`
    // el.style.backgroundPositionX = `${0}px, 105px, 210px`
    return geometry
  }

  get shouldLoadThumbnails() {
    const { asset, asDragPreview, isCapture } = this.props
    return !isCapture && !asDragPreview && (asset instanceof VideoAsset)
  }

  get shouldLoadAssetMediaThumbnail() {
    const { asset } = this.props
    return ((asset instanceof VideoAsset
        || asset instanceof AudioAsset
        || asset instanceof ImageAsset))
  }

  getRootStyles(data = {}) {
    const { asset, scale, style, getSourceFileById } = this.props
    const transitionGeometryOffset = this.getTransitionGeometryOffset()
    const left = time2Pixel(asset.startTime, scale) + transitionGeometryOffset.left
    let width = time2Pixel(
      this.liveDuration ?? asset.duration,
      scale
    ) - transitionGeometryOffset.width

    if (asset instanceof TransitionAsset) {
      width = Math.max(width, TRANSITION_MIN_WIDTH)
    }

    if (width < 0) {
      width = 0
    }

    const borderTopWidth = width > 0 ? 1 : 0
    const borderLeftWidth = width > 0 ? 1 : 0
    const borderRightWidth = borderLeftWidth
    const borderBottomWidth = borderTopWidth

    const borderWidths = {
      borderTopWidth,
      borderLeftWidth,
      borderRightWidth,
      borderBottomWidth,
    }

    const styles = {
      left,
      width,
      ...borderWidths,
    }

    let backgroundStyles = {
      backgroundSize: `auto ${this.thumbHeight}px`,
      backgroundRepeat: asset instanceof ImageAsset ? 'no-repeat' : 'repeat',
      backgroundPositionX: 'left',
      backgroundOrigin: 'border-box',
    }
    // eslint-disable-next-line react/destructuring-assignment
    const thumbnails = data.nextThumbnails || this.state.thumbnails
    const thumbnailsLoaded = data.nextLoaded !== undefined
      ? data.nextLoaded
      // eslint-disable-next-line react/destructuring-assignment
      : this.state.thumbnailsLoaded

    if (asset.mediaDeleted) {
      backgroundStyles = {
        backgroundImage: `${this.audioTrackStyle}url(${normalizeThumbnail('')})`,
      }
    } else if (!thumbnailsLoaded) {
      if (asset instanceof AudioAsset) {
        backgroundStyles = {}
      } else {
        const thumbnail = asset.thumbnail
        || (asset.fileId ? getSourceFileById(asset.fileId)?.thumbnail : undefined)
        if (thumbnails) {
          backgroundStyles = {
            ...backgroundStyles,
            backgroundImage: `${this.audioTrackStyle}url(${normalizeThumbnail(thumbnail)})`,
          }
        }
      }
    } else {
      const startIndexes = Object.keys(thumbnails)
      const minThumbIndex = Math.min(...startIndexes)
      const maxThumbIndex = Math.max(...startIndexes)
      const backgroundsSeries = []
      for (let i = minThumbIndex; i <= maxThumbIndex; i++) {
        const thumb = thumbnails[i] || asset.thumbnail
        backgroundsSeries.push(thumb)
      }
      const backgrounds = backgroundsSeries.map((base64, i) => `url(${normalizeThumbnail(base64)}) no-repeat border-box ${this.thumbWidth * i + this.thumbWidth * minThumbIndex}px / auto ${this.thumbHeight}px`)

      backgroundStyles = {
        background: `${this.audioTrackStyle} ${backgrounds.join(',')}`,
      }
    }

    return {
      ...styles,
      ...backgroundStyles,
      ...style,
    }
  }

  get isRecordingAssetLive() {
    const { asset, mediaRecordingAssetInProgress } = this.props
    return asset instanceof MediaRecordingAsset && mediaRecordingAssetInProgress
  }

  get liveDuration() {
    const { asset } = this.props
    const { context } = this
    return this.isRecordingAssetLive
      ? Math.max(0, context?.localTimeRef.current - asset.startTime)
      : undefined
  }

  get audioTrackStyle() {
    const { asset, muted } = this.props
    const { hover } = this.state
    if (asset.hasAudio && (!hover || asset.selected)) {
      return getAudioTrackStyle(!muted)
    }
    return ''
  }

  // TODO: move loadCachedThumbnails to Actions, LayerItem
  // will just display loaded thumbnails
  async loadCachedThumbnails(currentToken) {
    const self = this
    try {
      // eslint-disable-next-line

      const { asset, scale, timelineScrollPosition } = this.props

      if (asset.fileId === null || asset.mediaDeleted) {
        return
      }
      self.startIndex = 0
      const windowWidth = getWindowWidth()
      const progress = (asset.status === FILE_STATUS.PREPARE_THUMBNAILS) ? asset.progress : 100
      const visibleAssetWidth = Math.min(time2Pixel(asset.duration, scale), windowWidth)

      const endTimeline = pixel2Time(windowWidth + timelineScrollPosition, scale)
      const startTimeline = pixel2Time(timelineScrollPosition, scale)

      const numVisibleThumbnails = Math.round(
        Math.ceil(visibleAssetWidth / self.thumbWidth) * progress / 100 + 0.5
      )
      const numTotalThumbnails = Math.ceil(asset.duration / self.thumbWidth)
      if (!isOverlapAssets(startTimeline, endTimeline, asset.startTime, asset.endTime)) {
        return
      }
      const hiddenPart = time2Pixel(
        asset.startTime >= startTimeline
          ? 0
          : startTimeline - asset.startTime, scale
      )
      self.startIndex = Math.floor(hiddenPart / self.thumbWidth)

      const requests = []

      // console.log(`loadCachedThumbnails timelineScrollPosition: ${timelineScrollPosition} startIndex: ${this.startIndex} thumbnailsCount: ${thumbnailsCount}`)
      const newThumbs = {}
      let counter = 0
      for (let i = self.startIndex; i < numTotalThumbnails; i++) {
        if (counter > numVisibleThumbnails) {
          break
        }
        if (self.state.thumbnails[i]) {
          // If cached, no need to request
          continue
        }
        counter += 1
        const timestamp = Math.ceil(asset.mediaStart + pixel2Time(i * self.thumbWidth, scale))
        // console.log('loadCachedThumbnails make request')
        requests.push(
          getCachedThumbnail({
            fileId: asset.fileId,
            type: asset.type,
            time: timestamp,
            abortController: this.abortController,
          })
            .then(response => {
              if (response?.data?.data) {
                newThumbs[i] = response.data.data
              }
            })
          // eslint-disable-next-line
            .catch(err => {
              // console.error('Thumbnail fetch error', err)
            })
        )
      }

      for (let i = 0; i < requests.length; i += MAX_SIMULTANIOUS_THUMBS_REQ_COUNT) {
        const chunk = requests.slice(i, i + MAX_SIMULTANIOUS_THUMBS_REQ_COUNT)
        if (currentToken !== self.loadThumbnailsToken) {
          break
        }
        // eslint-disable-next-line
        await Promise.all(chunk)
      }

      if (!isEmpty(newThumbs) && requests.length && currentToken === self.loadThumbnailsToken) {
        this.setState(state => {
          const nextThumbnails = { ...state.thumbnails, ...newThumbs }

          const nextStyles = this.getRootStyles({
            nextThumbnails,
            nextLoaded: true,
          })

          return {
            thumbnailsLoaded: true,
            thumbnails: nextThumbnails,
            computedStyles: nextStyles,
          }
        })
      }
    } catch (e) {
      console.error(e)
    }
  }

  async loadAssetMediaThumbnail() {
    const { asset, updateAssetThumbnail } = this.props
    if (!asset.thumbnail) {
      const thumbnail = await getAssetMediaThumbnailFromBackend(asset)
      if (thumbnail) {
        updateAssetThumbnail(asset.id, thumbnail)
      }
    }
  }

  startResizing = () => {
    const { onDisableDrag } = this.props
    this.setState({ isResizing: true })
    onDisableDrag()
  }

  endResizing = () => {
    const { onEnableDrag } = this.props
    this.setState({ isResizing: false })
    onEnableDrag()
  }

  // TODO: move loadCachedThumbnails to Actions, LayerItem

  connectAssetDropTarget = ref => {
    const { connectDragSource } = this.props
    this.itemRef = ref
    if (connectDragSource) {
      connectDragSource(this.itemRef)
    }
  }

  initItemWidth = () => {
    const { asset, scale } = this.props
    if (asset?.duration) {
      this.width = time2Pixel(asset.duration, scale)
    }
  }

}

LayerItem.contextType = SliderTimeContext

LayerItem.defaultProps = {
  onClick: () => { },
  onDisableDrag: () => { },
  onEnableDrag: () => { },
  connectDragSource: undefined,
  asDragPreview: false,
  timelineScrollPosition: 0,
  isDragging: false,
  transitionRight: null,
  transitionLeft: null,
  muted: false,
  updateAssetThumbnail: () => {},
  isMovingSlider: false,
  isProjectEditing: false,
  isCapture: false,
  selectedAssetsIds: [],
  isCapturedAsset: () => false,
  stopCapture: () => {},
  toggleSelect: () => {},
  onTranslate: () => {},
  getSourceFileById: () => {},
}

LayerItem.propTypes = {
  connectDragSource: PropTypes.func,
  onClick: PropTypes.func,
  onDisableDrag: PropTypes.func,
  onEnableDrag: PropTypes.func,
  asset: PT.LayerAsset.isRequired,
  scale: PropTypes.number.isRequired,
  transitionRight: PT.Asset,
  transitionLeft: PT.Asset,
  asDragPreview: PropTypes.bool,
  isDragging: PropTypes.bool,
  timelineScrollPosition: PropTypes.number,
  muted: PropTypes.bool,
  updateAssetThumbnail: PropTypes.func,
  isMovingSlider: PropTypes.bool,
  isProjectEditing: PropTypes.bool,
  isCapture: PropTypes.bool,
  isCapturedAsset: PropTypes.func,
  stopCapture: PropTypes.func,
  toggleSelect: PropTypes.func,
  selectedAssetsIds: PropTypes.arrayOf(PropTypes.string),
  onTranslate: PropTypes.func,
  getSourceFileById: PropTypes.func,
  mediaRecordingAssetInProgress: PropTypes.bool.isRequired,
  stopRecording: PropTypes.func.isRequired,
}

const mapStateToProps = state => ({
  scale: state.timeline.scale,
  transitionAssets: Selectors.getTransitionAssets(state),
  getSourceFileById: fileId => Selectors.getSourceFileById(state, fileId),
  mediaRecordingAssetInProgress: Selectors.recording.isRecordingStarted(state)
    || Selectors.recording.isRecordingSaving(state),
})

const mapDispatchToProps = dispatch => ({
  onClick: (id, modifiers) => {
    dispatch(Actions.layer.selectAsset(id, modifiers))
  },
  updateAssetThumbnail: (assetId, thumbnail) => dispatch(
    Actions.layer.updateAssetInPreview(assetId, { thumbnail })
  ),
  toggleSelect: assetId => dispatch(Actions.layer.toggleAssetsSelected([ assetId ])),
  stopRecording: () => dispatch(Actions.recording.stopRecording()),
})

function LayerItemContainer(props) {
  const assetTransitions = useSelector(state => (
    Selectors.getTransitionsByAssetId(state, props.asset.id, { named: true })
  ))
  const { scrollLeft } = React.useContext(TimelineScrollPositionContext)
  const { timelineWidth } = React.useContext(TimelineGeometryContext)
  const muted = useSelector(
    state => props.asDragPreview
      ? undefined
      : Selectors.getLayerById(state, props.asset.layerId).muted
  )
  const isMovingSlider = useSelector(Selectors.getIsMovingSlider)
  const isProjectEditing = useSelector(Selectors.project.isProjectEditing)
  const isCapture = useSelector(Selectors.recording.isRecordingCapture)
  const stopCapture = useAction(stopLiveCapture)

  return (
    <LayerItem
      {...props}
      timelineScrollPosition={scrollLeft}
      transitionLeft={assetTransitions.left}
      transitionRight={assetTransitions.right}

      // This prop isn't used by component directly,
      // but serves as cache-buster, to re-render item when timeline is resized,
      // to update item geometry respectively
      timelineWidth={timelineWidth}
      muted={muted}
      isMovingSlider={isMovingSlider}
      isProjectEditing={isProjectEditing}
      isCapture={isCapture}
      isCapturedAsset={isCapturedAsset}
      stopCapture={stopCapture}
    />
  )
}

const arePropsEqual = (prev, next) => (
  prev.scale === next.scale
    && prev.asset.id === next.asset.id
    && prev.asset.startTime === next.asset.startTime
    && prev.asset.duration === next.asset.duration
    && prev.asset.layerId === next.asset.layerId
    && JSON.stringify(
      prev.selectedAssetsIds
    ) === JSON.stringify(next.selectedAssetsIds) && next.isPlaying
)

export default connect(mapStateToProps,
  mapDispatchToProps)(React.memo(LayerItemContainer, arePropsEqual))
